import { useEffect, useRef } from "react";
import type { SmartNumber } from "@/features/sub-families/types";
import { smartNumberSetMappingAction, smartNumberSetSubFamilyAction } from "@/redux/slices/smart-number-slice.ts";
import { useAppDispatch, useAppSelector } from "@/redux/store.ts";
import HistoryPatch from "../lib/patch-history.ts";

const historyPatch = new HistoryPatch();

export default function useHistoryState() {
    const dispatch = useAppDispatch();
    const calculatedResult = useAppSelector((state) => state.smartNumber.calculatedResult);
    const mapping = useAppSelector((state) => state.smartNumber.mapping);
    const subFamily = useAppSelector((state) => state.smartNumber.subFamily);

    const hasInitializedRef = useRef<boolean>(false);

    useEffect(() => {
        if (!calculatedResult || !mapping) return;

        // popstate sets currentSmartNumber before Redux recalculates; matching means
        // restore is done. Clear #restoring so the host page's pushState works again.
        if (calculatedResult === historyPatch.currentSmartNumber) {
            historyPatch.restoring = false;
            return;
        }

        historyPatch.currentSmartNumber = calculatedResult;

        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            historyPatch.replace(calculatedResult, mapping);
            historyPatch.restoring = false;
            return;
        }

        historyPatch.push(calculatedResult, mapping);
        historyPatch.restoring = false;
    }, [calculatedResult, mapping]);

    useEffect(() => {
        const abortController = new AbortController();

        window.addEventListener(
            "popstate",
            (evt) => {
                const entry = historyPatch.resolve(evt.state);

                if (!entry) return;
                if (entry.smartNumber === historyPatch.currentSmartNumber) return;

                historyPatch.restoring = true;
                historyPatch.currentSmartNumber = entry.smartNumber;

                if (entry.smartNumber === subFamily) {
                    dispatch(smartNumberSetMappingAction(entry.mapping));
                } else {
                    dispatch(smartNumberSetSubFamilyAction(entry.smartNumber as SmartNumber));
                }
            },
            { signal: abortController.signal },
        );

        return () => abortController.abort();
    }, [dispatch, subFamily]);
}
